import React from 'react';

// Components
import OperatorsAndVehicleForm from '../../../../../components/forms/ActionForms/OperatorsAndVehicles';

// Types
import { OperatorsAndVehiclesFormValues } from '../../../../../components/forms/ActionForms/OperatorsAndVehicles/types';
import { NewRouteLoaderData } from '../../types';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { SR_OPERATORS_FORM_KEY } from '../../NewRoute/saveData';

// Styles
import { ContentArea, MainContent } from '../MainStyledComponents';

// ═══════════════════════════════════════════════════════════════════
// buildOperatorsInitialData — helper puro para el `initialData` que
// `NewRoute/index.tsx` pasa como `baseInitialData` a este componente.
//
// Extraído de `NewRoute/index.tsx` (Task 3 del refactor de patrones RV,
// 2026-07-07) — misma lógica, sin cambios de comportamiento.
// ═══════════════════════════════════════════════════════════════════

export const buildOperatorsInitialData = (
  loaderData: NewRouteLoaderData,
): OperatorsAndVehiclesFormValues => {
  const concessions = loaderData?.concessions || [];

  return {
    operatorsTable: [],
    vehiclesTable: [],
    vehicleTable: {},
    externals: [],
    externalCompanies: loaderData?.externalCompanies || [],
    registerInitDate: '',
    registerEndDate: '',
    // Campo nuevo introducido en main PR #33 (registerArrivalDate). En el
    // wizard de Nou Recorregut no se usa todavía — arranca vacío y se
    // ignora downstream.
    registerArrivalDate: '',
    finishTime: '',
    totalHours: '',
    hoursType: '',
    isExternal: false,
    hoursTypeOptions: loaderData?.hoursTypeOptions || [],
    internalOperators: loaderData?.internalOperators || {
      operators: [],
      vehicles: [],
      teams: [],
    },
    externalOperators: { operators: [], vehicles: [] },
    previousFormData: {} as FormData,
    breadCrumbsData: {
      orderParentId: '',
      orderParentRoid: '',
      concessions: [],
      concession: '',
    },
    formValue: undefined,
    concessions: concessions.map((c) => ({
      id: c.id,
      name: c.name,
      vehicleIDs: c.vehicleIDs || [],
    })),
    teams: loaderData?.teams || [],
  };
};

// ═══════════════════════════════════════════════════════════════════
// TAB OPERARIS — contenido con rehidratación de borrador
//
// Extraído de `NewRoute/index.tsx` (Task 3 del refactor de patrones RV,
// 2026-07-07) — misma lógica, sin cambios de comportamiento.
// ═══════════════════════════════════════════════════════════════════

/**
 * Tour P5 (2026-07-07): las Tabs solo montan la pestaña seleccionada, así
 * que al cambiar de tab el form de Operarios se DESMONTA y perdía la
 * selección. `useCacheFormInSessionStorage` (dentro del form) persiste cada
 * cambio; este wrapper REHIDRATA el borrador al volver a montar — el mismo
 * patrón save-data/pass-data del wizard de actuaciones (su loader lee la
 * key de sessionStorage y la funde en `initialData`).
 */
export interface OperarisTabContentProps {
  baseInitialData: OperatorsAndVehiclesFormValues;
  operatorsErrors: string[];
  onCrewChange: (crew: {
    operators: number;
    vehicles: number;
    vehiclesWithoutDriver: number;
  }) => void;
  /** Key sessionStorage del borrador de esta tab. Por defecto la del wizard
   *  "Nuevo recorrido" (SR_OPERATORS_FORM_KEY). El wizard EditRoute (F4
   *  2026-07-17) pasa la key EDIT para que su borrador NO pise el de creación
   *  — se usa tanto para rehidratar como para persistir (namingPersistForm). */
  operatorsFormKey?: string;
  /** Addendum RV 2026-07-20: reenvía a `OperatorsAndVehicleForm` la señal de
   *  contexto que muestra el selector activos/inactivos/todos de operarios y
   *  vehículos (hoy gated solo por URL `actions-validation`). Solo la pasa
   *  `NewRoutePage` en modo edición del RV. */
  showCrewStateFilter?: boolean;
}

export default function OperarisTabContent({
  baseInitialData,
  operatorsErrors,
  onCrewChange,
  operatorsFormKey = SR_OPERATORS_FORM_KEY,
  showCrewStateFilter,
}: OperarisTabContentProps): JSX.Element {
  // Lectura ÚNICA al montar la tab (el componente se remonta en cada cambio
  // de pestaña, así que siempre lee el borrador más reciente).
  const initialData = React.useMemo(() => {
    try {
      const raw = sessionStorage.getItem(operatorsFormKey);
      if (raw) {
        const parsed = JSON.parse(decompressSessionStorage(raw));
        // BUG borrador RV (2026-07-12): borradores envenenados por la antigua
        // caché global de conductores traían entradas fantasma `{}` en
        // vehicleTable (sin vehicleID) que aquí se rehidrataban como
        // vehículos pre-seleccionados. Solo entra una entrada si identifica
        // a su vehículo — el handler de selección real siempre lo setea.
        const vehicleTable = Object.fromEntries(
          Object.entries(
            (parsed?.vehicleTable ?? {}) as Record<string, any>,
          ).filter(([, v]) => v && (v.vehicleID || v.id || v.driverID)),
        );
        return {
          ...baseInitialData,
          operatorsTable: parsed?.operatorsTable ?? [],
          vehiclesTable: parsed?.vehiclesTable ?? [],
          vehicleTable,
        };
      }
    } catch (e) {
      console.warn('[NewRoute] no se pudo rehidratar el borrador de operarios:', e);
    }
    return baseInitialData;
  }, []);

  return (
    <ContentArea>
      <MainContent>
        <OperatorsAndVehicleForm
          onFormSubmit={() => {}}
          initialData={initialData}
          namingPersistForm={operatorsFormKey}
          // V4 FR-3.2 (audit Disc#9/#11): RV solo admite operaris interns
          // (sin empresas externas) y NO usa Distribució d'hores. El switch
          // "Actuació simultània" tampoco aplica aquí (es concepto de
          // Actuación — P9 sí lo muestra).
          hideExternalsTab
          hideHoursDistribution
          hideSimultaneous
          hideSubmitButton
          // Tour P5 (feedback usuario): los fallos FR-3.4 se pintan DENTRO
          // de la card lila, en el mismo slot rojo que usa operatorError, y
          // se recalculan EN VIVO al cambiar la tripulación.
          externalErrors={operatorsErrors}
          onCrewChange={onCrewChange}
          showCrewStateFilter={showCrewStateFilter}
        />
      </MainContent>
    </ContentArea>
  );
}
